import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { masterApi } from "@/contexts/MasterAuthContext";
import { Check, ChevronLeft, ChevronRight, Sparkles, Image, Globe, Shield, CreditCard, Trophy, Upload, Loader2, X } from "lucide-react";

const STEPS = [
    { key: "basic",    label: "Temel Bilgiler",  icon: Sparkles },
    { key: "visual",   label: "Görsel Kimlik",   icon: Image },
    { key: "mackolik", label: "Lig Verisi",      icon: Trophy },
    { key: "admin",    label: "Süper Admin",     icon: Shield },
    { key: "plan",     label: "Paket",           icon: CreditCard },
    { key: "review",   label: "Onay",            icon: Check },
];

export default function MasterOnboarding() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [plans, setPlans] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null);

    const [form, setForm] = useState({
        name: "", slug: "", short_name: "", description: "",
        domains: "",
        logo_url: "", favicon_url: "",
        primary_color: "#f5dc4c", secondary_color: "#ffffff", accent_color: "#0b0b0b", bg_color: "#0b0b0b",
        font_family: "inherit", theme_template: "classic",
        instagram_handle: "", website_url: "",
        mackolik_team_id: "", sporzip_slug: "", mackolik_team_name: "",
        subscription_plan: "starter",
        admin_email: "", admin_password: "", admin_name: "",
        seed_demo_data: true,
        trigger_mackolik_first_sync: true,
    });

    useEffect(() => {
        masterApi.get("/master/plans").then((r) => setPlans(r.data)).catch(() => {});
        masterApi.get("/master/templates").then((r) => setTemplates(r.data)).catch(() => {});
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = (stepIdx) => {
        if (stepIdx === 0) {
            if (!form.name.trim() || !form.slug.trim()) return "Kulüp adı ve slug zorunludur";
            if (!/^[a-z0-9-]+$/.test(form.slug)) return "Slug sadece küçük harf, rakam ve - içerebilir";
        }
        if (stepIdx === 3) {
            if (!form.admin_email.trim() || !form.admin_password.trim()) return "Süper admin e-posta ve şifre zorunludur";
            if (form.admin_password.length < 6) return "Şifre en az 6 karakter olmalı";
        }
        return null;
    };

    const next = () => {
        const err = validate(step);
        if (err) { setError(err); return; }
        setError("");
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };
    const prev = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

    const submit = async () => {
        setSubmitting(true);
        setError("");
        try {
            const payload = {
                ...form,
                domains: form.subscription_plan === "pro"
                    ? form.domains.split(",").map((d) => d.trim()).filter(Boolean)
                    : [],
            };
            const r = await masterApi.post("/master/clubs", payload);
            setSuccess(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Kayıt başarısız");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div data-testid="onboarding-success" className="max-w-2xl mx-auto">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-4">
                        <Check size={32} className="text-emerald-400" />
                    </div>
                    <h2 className="text-3xl font-extrabold mb-2">Kulüp başarıyla oluşturuldu!</h2>
                    <p className="text-neutral-400 mb-6">{success.message}</p>
                    <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-5 text-left text-sm space-y-2">
                        <div><span className="text-neutral-500">Kulüp:</span> <strong>{success.club.name}</strong></div>
                        <div><span className="text-neutral-500">ID/Slug:</span> <code className="text-yellow-400">{success.club.id}</code></div>
                        <div><span className="text-neutral-500">Admin Email:</span> {success.admin.email}</div>
                        <div><span className="text-neutral-500">Domains:</span> {(success.club.domains || []).join(", ") || "—"}</div>
                    </div>
                    <div data-testid="onboarding-login-info" className="bg-[#0a0a0a] border border-yellow-500/30 rounded-xl p-5 text-left text-sm space-y-2 mt-4">
                        <div className="font-bold uppercase tracking-widest text-yellow-400 text-xs">Kulübe Gönderilecek Giriş Bilgileri</div>
                        <div><span className="text-neutral-500">Panel adresi:</span>{" "}
                            <a className="text-yellow-400 underline" target="_blank" rel="noreferrer" href="https://draifutbol.com/admin">draifutbol.com/admin</a>
                        </div>
                        <div><span className="text-neutral-500">Kullanıcı:</span> {success.admin.email}</div>
                        {(success.club.domains || []).length > 0 && (
                            <div><span className="text-neutral-500">Web sitesi (Pro):</span> {(success.club.domains || []).join(", ")}</div>
                        )}
                        <p className="text-xs text-neutral-500 pt-2">Tüm kulüpler aynı adresten giriş yapar; hangi kulübe ait oldukları hesaplarından otomatik anlaşılır. Web sitesi yalnızca Pro pakete dahildir.</p>
                    </div>
                    <div className="flex gap-3 justify-center mt-6">
                        <button data-testid="goto-clubs-btn" onClick={() => navigate("/master/clubs")} className="px-5 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold uppercase tracking-wider text-sm rounded-lg">Kulüpler Listesine Dön</button>
                        <button onClick={() => { setSuccess(null); setStep(0); setForm({ ...form, name: "", slug: "", admin_email: "", admin_password: "" }); }} className="px-5 py-3 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-sm rounded-lg">Yeni Kulüp Daha Ekle</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="onboarding-wizard" className="max-w-4xl mx-auto">
            <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-2">Onboarding Wizard</div>
                <h1 className="text-4xl font-extrabold tracking-tight">Yeni Kulüp Ekle</h1>
                <p className="text-neutral-400 mt-2">6 adımda kulübünüzü platformumuza entegre edin</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-between mb-10 overflow-x-auto pb-2">
                {STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const done = i < step;
                    const active = i === step;
                    return (
                        <div key={s.key} className="flex items-center flex-shrink-0">
                            <div className={`flex flex-col items-center ${i > 0 ? "ml-2" : ""}`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-emerald-500 border-emerald-500 text-black" : active ? "bg-yellow-500 border-yellow-500 text-black" : "border-[#2a2a2a] text-neutral-500"}`}>
                                    {done ? <Check size={16} /> : <Icon size={16} />}
                                </div>
                                <div className={`text-xs mt-2 text-center whitespace-nowrap ${active ? "text-yellow-400 font-bold" : done ? "text-emerald-400" : "text-neutral-600"}`}>{s.label}</div>
                            </div>
                            {i < STEPS.length - 1 && <div className={`w-12 h-0.5 mx-2 ${done ? "bg-emerald-500" : "bg-[#2a2a2a]"}`} />}
                        </div>
                    );
                })}
            </div>

            {/* Form */}
            <div data-testid={`wizard-step-${STEPS[step].key}`} className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-8 mb-6 min-h-[400px]">
                {error && <div data-testid="wizard-error" className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-5 text-sm">{error}</div>}

                {/* Step 1: Basic */}
                {step === 0 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Temel Bilgiler</h3>
                        <Field label="Kulüp Tam Adı*" testId="field-name">
                            <input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="liv-master-input" placeholder="Örn: Beşiktaş Futbol Kulübü" />
                        </Field>
                        <Field label="Slug (URL/ID)*" hint="Sadece küçük harf, rakam ve - kullanın">
                            <input data-testid="input-slug" value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="liv-master-input" placeholder="ornek-fk" />
                        </Field>
                        <Field label="Kısa İsim">
                            <input data-testid="input-short-name" value={form.short_name} onChange={(e) => set("short_name", e.target.value)} className="liv-master-input" placeholder="Beşiktaş" />
                        </Field>
                        <Field label="Açıklama">
                            <textarea data-testid="input-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="liv-master-input" rows={3} placeholder="Kulüp hakkında kısa açıklama" />
                        </Field>
                    </div>
                )}

                {/* Step 2: Visual */}
                {step === 1 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Görsel Kimlik</h3>
                        <Field label="Logo" hint="Dosya yükleyin (PNG/SVG/JPG · max 10MB) veya tam URL yapıştırın">
                            <ImageUploadField
                                value={form.logo_url}
                                onChange={(v) => set("logo_url", v)}
                                purpose="logo"
                                testIdPrefix="logo"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            />
                        </Field>
                        <Field label="Favicon" hint="Tarayıcı sekme ikonu (genelde 32×32 veya 64×64 PNG/ICO)">
                            <ImageUploadField
                                value={form.favicon_url}
                                onChange={(v) => set("favicon_url", v)}
                                purpose="favicon"
                                testIdPrefix="favicon"
                                accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
                            />
                        </Field>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[["primary_color","Primary"],["secondary_color","Secondary"],["accent_color","Accent"],["bg_color","Background"]].map(([k, lbl]) => (
                                <Field key={k} label={lbl}>
                                    <div className="flex items-center gap-2">
                                        <input data-testid={`input-${k}`} type="color" value={form[k]} onChange={(e) => set(k, e.target.value)} className="w-12 h-10 rounded cursor-pointer bg-transparent" />
                                        <input value={form[k]} onChange={(e) => set(k, e.target.value)} className="liv-master-input flex-1 font-mono text-sm" />
                                    </div>
                                </Field>
                            ))}
                        </div>
                        <Field label="Homepage Şablonu" hint="Yalnızca Pro pakette kullanılır (web sitesi Pro'ya özel)">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {templates.map((t) => (
                                    <button key={t.key} type="button" data-testid={`template-${t.key}`} onClick={() => set("theme_template", t.key)} className={`text-left p-4 rounded-xl border-2 transition-all ${form.theme_template === t.key ? "border-yellow-500 bg-yellow-500/5" : "border-[#2a2a2a] hover:border-[#3a3a3a]"}`}>
                                        <div className="font-bold text-sm">{t.name}</div>
                                        <div className="text-xs text-neutral-500 mt-1 line-clamp-3">{t.description}</div>
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Instagram Handle">
                            <input data-testid="input-instagram" value={form.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} className="liv-master-input" placeholder="@kulup_resmi" />
                        </Field>
                        <Field label="Website URL">
                            <input data-testid="input-website" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} className="liv-master-input" placeholder="https://kulup.org" />
                        </Field>
                    </div>
                )}

                {/* Step 3: League data source */}
                {step === 2 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Lig Verisi (Sporzip)</h3>
                        <p className="text-sm text-neutral-400">Kulübün <b>sporzip.com</b> üzerindeki takım sayfasını girin. Puan durumu, fikstür ve kadro buradan otomatik senkronlanır. (Opsiyonel)</p>
                        <Field label="Sporzip Takım Adresi" hint="sporzip.com/livanespor → 'livanespor'. Boş bırakırsanız takım adından üretilir.">
                            <input data-testid="input-sporzip-slug" value={form.sporzip_slug} onChange={(e) => set("sporzip_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="liv-master-input" placeholder="ornek-spor" />
                        </Field>
                        <Field label="Takım Adı (Sporzip'teki yazılışla)">
                            <input data-testid="input-mackolik-name" value={form.mackolik_team_name} onChange={(e) => set("mackolik_team_name", e.target.value)} className="liv-master-input" placeholder="Örn: Livanespor" />
                        </Field>
                    </div>
                )}

                {/* Step 4: Admin */}
                {step === 3 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Süper Admin Hesabı</h3>
                        <p className="text-sm text-neutral-400">Bu kulübün panelini yönetecek süper admin hesabı bilgileri. Hesap oluşturulduktan sonra kulüp domaininden /admin'e giderek login olabilir.</p>
                        <Field label="Admin E-posta*">
                            <input data-testid="input-admin-email" type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} className="liv-master-input" placeholder="admin@kulup.com" />
                        </Field>
                        <Field label="Admin Şifre*" hint="En az 6 karakter">
                            <input data-testid="input-admin-password" type="text" value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} className="liv-master-input" placeholder="Güçlü şifre" />
                        </Field>
                        <Field label="Admin Adı">
                            <input data-testid="input-admin-name" value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} className="liv-master-input" placeholder="Süper Admin" />
                        </Field>
                    </div>
                )}

                {/* Step 5: Plan */}
                {step === 4 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Abonelik Paketi</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {plans.map((p) => (
                                <button key={p.key} type="button" data-testid={`plan-${p.key}`} onClick={() => set("subscription_plan", p.key)} className={`text-left p-5 rounded-xl border-2 transition-all ${form.subscription_plan === p.key ? "border-yellow-500 bg-yellow-500/5" : "border-[#2a2a2a] hover:border-[#3a3a3a]"}`}>
                                    <div className="font-extrabold text-lg uppercase">{p.display_name}</div>
                                    <div className="text-2xl font-extrabold text-yellow-400 mt-1">₺{Number(p.monthly_price_try || 0).toLocaleString("tr-TR")}<span className="text-xs text-neutral-500">/ay</span></div>
                                    <div className="text-xs text-neutral-400 mt-2">{p.monthly_credit_limit} AI kredi / ay</div>
                                </button>
                            ))}
                        </div>
                        {form.subscription_plan === "pro" ? (
                            <div className="pt-4 border-t border-[#2a2a2a] space-y-4">
                                <div className="text-xs uppercase tracking-widest text-yellow-400 font-bold">Pro — Web Sitesi</div>
                                <Field label="Domain(ler)" hint={`Virgülle ayrılmış. Boş bırakırsanız kulüp ${form.slug || "slug"}.draifutbol.com adresinden yayına girer.`}>
                                    <input data-testid="input-domains" value={form.domains} onChange={(e) => set("domains", e.target.value)} className="liv-master-input" placeholder="ornek.com, www.ornek.com" />
                                </Field>
                            </div>
                        ) : (
                            <div className="pt-4 border-t border-[#2a2a2a] text-sm text-neutral-400" data-testid="plan-no-website-note">
                                Bu pakette kulübün <b>web sitesi açılmaz</b> — kulüp yalnızca yönetim panelini kullanır. Web sitesi ve özel domain yalnızca <b className="text-yellow-400">Pro</b> pakete dahildir.
                            </div>
                        )}
                    </div>
                )}

                {/* Step 6: Review */}
                {step === 5 && (
                    <div className="space-y-5">
                        <h3 className="font-bold text-xl mb-4">Özet & Onay</h3>
                        <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-5 space-y-3 text-sm">
                            <SummaryRow label="Kulüp Adı" value={form.name} />
                            <SummaryRow label="Slug" value={form.slug} />
                            <SummaryRow label="Domain(ler)" value={form.subscription_plan === "pro" ? (form.domains || "—") : "Web sitesi yok (Pro'ya özel)"} />
                            <SummaryRow label="Tema Şablonu" value={templates.find(t => t.key === form.theme_template)?.name || form.theme_template} />
                            <SummaryRow label="Renk Paleti" value={
                                <div className="flex gap-1">
                                    <span className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: form.primary_color }} />
                                    <span className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: form.secondary_color }} />
                                    <span className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: form.bg_color }} />
                                </div>
                            } />
                            <SummaryRow label="Paket" value={plans.find(p => p.key === form.subscription_plan)?.display_name || form.subscription_plan} />
                            <SummaryRow label="Admin Email" value={form.admin_email} />
                            <SummaryRow label="Lig Verisi (Sporzip)" value={form.mackolik_team_name || "—"} />
                        </div>

                        {/* Post-onboarding options */}
                        <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-5 space-y-3">
                            <h4 className="font-bold text-sm uppercase tracking-widest text-neutral-400 mb-2">Oluşturma Sonrası</h4>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input data-testid="seed-demo-data" type="checkbox" checked={form.seed_demo_data} onChange={(e) => set("seed_demo_data", e.target.checked)} className="w-5 h-5 mt-0.5" />
                                <div>
                                    <div className="font-medium text-sm">Demo verisi yükle</div>
                                    <div className="text-xs text-neutral-500">4 örnek oyuncu, 2 maç (1 geçmiş + 1 gelecek), 1 hoş geldin haberi, 1 sponsor ve 3 örnek mali işlem. Admin paneli boş açılmasın — kulüp sahibi düzenleyebilir veya silebilir.</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input data-testid="trigger-mackolik-sync" type="checkbox" checked={form.trigger_mackolik_first_sync} onChange={(e) => set("trigger_mackolik_first_sync", e.target.checked)} className="w-5 h-5 mt-0.5" disabled={!form.mackolik_team_name} />
                                <div>
                                    <div className="font-medium text-sm">İlk lig verisi senkronunu başlat {!form.mackolik_team_name && <span className="text-neutral-500">(Takım adı girilmemiş)</span>}</div>
                                    <div className="text-xs text-neutral-500">Onboarding tamamlandıktan hemen sonra arka planda Sporzip'ten kadro, fikstür ve puan durumu çekilir. Süper admin paneli ilk açtığında veriler hazır olur.</div>
                                </div>
                            </label>
                        </div>

                        <button data-testid="wizard-submit" disabled={submitting} onClick={submit} className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest rounded-xl transition-all">
                            {submitting ? "Oluşturuluyor…" : "Kulübü Oluştur"}
                        </button>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
                <button data-testid="wizard-prev" disabled={step === 0} onClick={prev} className="inline-flex items-center gap-2 px-5 py-3 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-sm rounded-lg disabled:opacity-30 transition-colors">
                    <ChevronLeft size={16}/> Geri
                </button>
                {step < STEPS.length - 1 && (
                    <button data-testid="wizard-next" onClick={next} className="inline-flex items-center gap-2 px-5 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold uppercase tracking-wider text-sm rounded-lg transition-all">
                        Devam <ChevronRight size={16}/>
                    </button>
                )}
            </div>

            <style>{`
                .liv-master-input { width: 100%; background: #0a0a0a; border: 1px solid #2a2a2a; padding: 0.65rem 0.9rem; border-radius: 0.5rem; color: #fff; outline: none; transition: border-color .15s; }
                .liv-master-input:focus { border-color: #f5dc4c; }
            `}</style>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-neutral-300 mb-2">{label}</label>
            {children}
            {hint && <p className="text-xs text-neutral-500 mt-1.5">{hint}</p>}
        </div>
    );
}

function SummaryRow({ label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-[#1f1f1f] pb-2 last:border-0">
            <span className="text-neutral-500">{label}</span>
            <span className="font-medium">{value}</span>
        </div>
    );
}

function ImageUploadField({ value, onChange, purpose = "logo", testIdPrefix = "image", accept = "image/*" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError("Dosya boyutu 10MB'ı aşamaz");
            return;
        }
        setError("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("purpose", purpose);
            const r = await masterApi.post("/master/media/upload", fd);
            onChange(r.data.public_url);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Yükleme başarısız");
        } finally {
            setUploading(false);
        }
    };

    const previewSrc = value
        ? (value.startsWith("http") ? value : `${backendBase}${value}`)
        : null;

    return (
        <div className="space-y-2">
            <div className="flex items-stretch gap-3">
                <div className="w-20 h-20 rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {previewSrc ? (
                        <img src={previewSrc} alt={`${testIdPrefix} önizleme`} className="w-full h-full object-contain" data-testid={`${testIdPrefix}-preview`} />
                    ) : (
                        <Image size={22} className="text-neutral-600" />
                    )}
                </div>
                <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid={`${testIdPrefix}-upload-btn`}
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black text-xs font-bold uppercase tracking-wider rounded-lg transition-colors"
                        >
                            {uploading ? <><Loader2 size={14} className="animate-spin" /> Yükleniyor…</> : <><Upload size={14} /> Dosya Seç</>}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                data-testid={`${testIdPrefix}-clear-btn`}
                                className="inline-flex items-center gap-1 px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-xs rounded-lg transition-colors"
                            >
                                <X size={12} /> Kaldır
                            </button>
                        )}
                        <input
                            ref={inputRef}
                            data-testid={`${testIdPrefix}-file-input`}
                            type="file"
                            accept={accept}
                            className="hidden"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                    </div>
                    <input
                        data-testid={`input-${testIdPrefix}-url`}
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="liv-master-input text-sm"
                        placeholder="…veya tam URL yapıştırın (https://…)"
                    />
                </div>
            </div>
            {error && <div data-testid={`${testIdPrefix}-upload-error`} className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">{error}</div>}
        </div>
    );
}
